/* eslint-disable max-lines */
import { Navigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { I18nContainer } from '@containers/i18n.container'
import { RootElement } from '@containers/root-element.container'
import { ErrorBoundary } from '@components/ErrorBoundary'

import type { RouteObject } from 'react-router'

// Config brute passée à `createBrowserRouter` par `router.container.tsx`. Extraite dans son propre fichier (sans
// composant) pour que `router.container.test.tsx` puisse l'importer et la résoudre via `matchRoutes` sans monter
// l'app — react-refresh (`only-export-components`) interdit sinon un fichier qui exporte à la fois un composant
// et une constante.
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootElement />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: ':language',
        element: <I18nContainer />,
        children: [
          {
            path: 'projects/access-denied',
            async lazy() {
              const { GuardAccessDeniedPage } = await import('@modules/project/pages/access-denied')
              return { Component: GuardAccessDeniedPage }
            },
          },
          {
            path: 'projects',
            async lazy() {
              const { GuardProjectsPage } = await import('@modules/project/pages/view')
              return { Component: GuardProjectsPage }
            },
          },
          {
            path: '',
            async lazy() {
              const { GuardMainPage } = await import('@modules/main/pages/main')
              return { Component: GuardMainPage }
            },
            children: [
              {
                path: 'projects/:projectId',
                async lazy() {
                  const { GuardMainPage } = await import('@modules/project/pages/main')
                  return { Component: GuardMainPage }
                },
                children: [
                  {
                    path: 'files/:projectFileId',
                    async lazy() {
                      const { ProjectFileOpenPage } = await import('@modules/project-files/pages/open')
                      return { Component: ProjectFileOpenPage }
                    },
                  },
                  {
                    path: 'view',
                    async lazy() {
                      const { GuardMainPage } = await import('@modules/project/pages/view/id')
                      return { Component: GuardMainPage }
                    },
                  },
                  {
                    path: 'statistics',
                    children: [
                      {
                        path: '',
                        async lazy() {
                          const { GuardStatisticsPage } = await import('@modules/project/pages/statistics')
                          return { Component: GuardStatisticsPage }
                        },
                      },
                      {
                        path: 'observations',
                        async lazy() {
                          const { GuardStatisticsObservationsPage } = await import(
                            '@modules/project/pages/statistics-observations'
                          )
                          return { Component: GuardStatisticsObservationsPage }
                        },
                      },
                      {
                        path: 'formulaires',
                        async lazy() {
                          const { GuardStatisticsFormulairesPage } = await import(
                            '@modules/project/pages/statistics-formulaires'
                          )
                          return { Component: GuardStatisticsFormulairesPage }
                        },
                      },
                      {
                        path: 'infos',
                        async lazy() {
                          const { GuardStatisticsInfosPage } = await import('@modules/project/pages/statistics-infos')
                          return { Component: GuardStatisticsInfosPage }
                        },
                      },
                      {
                        path: 'tasks',
                        async lazy() {
                          const { GuardStatisticsTasksPage } = await import('@modules/project/pages/statistics-tasks')
                          return { Component: GuardStatisticsTasksPage }
                        },
                      },
                    ],
                  },
                  {
                    path: 'statistics/visas',
                    async lazy() {
                      const { GuardStatisticsVisasPage } = await import('@modules/project/pages/statistics-visas')
                      return { Component: GuardStatisticsVisasPage }
                    },
                  },
                  {
                    path: 'zoning',
                    async lazy() {
                      const { GuardZoningPage } = await import('@modules/project-zoning/pages/view')
                      return { Component: GuardZoningPage }
                    },
                  },
                  {
                    path: 'companies',
                    async lazy() {
                      const { GuardCompaniesPage } = await import('@modules/project-companies/pages/view')
                      return { Component: GuardCompaniesPage }
                    },
                  },
                  {
                    path: 'trades',
                    async lazy() {
                      const { GuardTradesPage } = await import('@modules/project-trades/pages/view')
                      return { Component: GuardTradesPage }
                    },
                  },
                  {
                    path: 'phases',
                    async lazy() {
                      const { GuardPhasesPage } = await import('@modules/project-phases/pages/view')
                      return { Component: GuardPhasesPage }
                    },
                  },
                  {
                    path: 'library',
                    async lazy() {
                      const { GuardLibraryPage } = await import('@modules/project-library/pages/view')
                      return { Component: GuardLibraryPage }
                    },
                    children: [
                      {
                        path: 'task-templates',
                        async lazy() {
                          const { TaskTemplatesPage } = await import('@modules/project-library/pages/task-templates')
                          return { Component: TaskTemplatesPage }
                        },
                      },
                      {
                        path: 'sequence-templates',
                        async lazy() {
                          const { SequenceTemplatesPage } = await import(
                            '@modules/project-library/pages/sequence-templates'
                          )
                          return { Component: SequenceTemplatesPage }
                        },
                      },
                      {
                        path: '',
                        Component: () => <Navigate to="task-templates" />,
                      },
                    ],
                  },
                  {
                    path: 'form-models',
                    async lazy() {
                      const { GuardFormModelsPage } = await import('@modules/project-form-models/pages/view')
                      return { Component: GuardFormModelsPage }
                    },
                  },
                  {
                    path: 'plans',
                    children: [
                      {
                        path: '',
                        async lazy() {
                          const { GuardPlansPage } = await import('@modules/project-plans/pages/view')
                          return { Component: GuardPlansPage }
                        },
                      },
                      {
                        path: 'editor/:planId',
                        async lazy() {
                          const { GuardPlanEditorPage } = await import('@modules/project-plans/pages/editor')
                          return { Component: GuardPlanEditorPage }
                        },
                      },
                    ],
                  },
                  {
                    path: 'documents',
                    async lazy() {
                      const { GuardDocumentsPage } = await import('@modules/project-documents/pages/view')
                      return { Component: GuardDocumentsPage }
                    },
                  },
                  {
                    path: 'users',
                    async lazy() {
                      const { GuardUsersPage } = await import('@modules/project-users/pages/view')
                      return { Component: GuardUsersPage }
                    },
                  },
                  {
                    path: 'planning',
                    async lazy() {
                      const { BryntumTestPage } = await import('@modules/project-planning/pages/bryntum-test')
                      return { Component: BryntumTestPage }
                    },
                  },
                  {
                    path: 'planning/details/:taskId',
                    async lazy() {
                      const { PlanningTaskDetailsPage } = await import('@modules/project-planning/pages/task-details')
                      return { Component: PlanningTaskDetailsPage }
                    },
                  },
                  {
                    path: 'forms',
                    children: [
                      {
                        path: '',
                        async lazy() {
                          const { GuardFormsPage } = await import('@modules/project-forms/pages/view')
                          return { Component: GuardFormsPage }
                        },
                      },
                      {
                        path: 'details/:formId',
                        async lazy() {
                          const { DetailsPage } = await import('@modules/project-forms/pages/details')
                          return { Component: DetailsPage }
                        },
                      },
                    ],
                  },
                  {
                    path: 'observations',
                    children: [
                      {
                        path: '',
                        async lazy() {
                          const { GuardObservationsPage } = await import('@modules/project-observations/pages/view')
                          return { Component: GuardObservationsPage }
                        },
                      },
                      {
                        path: 'details/:observationId',
                        async lazy() {
                          const { DetailsPage } = await import('@modules/project-observations/pages/details')
                          return { Component: DetailsPage }
                        },
                      },
                    ],
                  },
                  {
                    path: 'visa-requests',
                    children: [
                      {
                        path: '',
                        async lazy() {
                          const { GuardValidationRequestsPage } = await import(
                            '@modules/project-validation-requests/pages/view'
                          )
                          return { Component: GuardValidationRequestsPage }
                        },
                      },
                      {
                        path: 'details/:validationRequestId',
                        async lazy() {
                          const { DetailsPage } = await import('@modules/project-validation-requests/pages/details')
                          return { Component: DetailsPage }
                        },
                      },
                    ],
                  },
                  {
                    path: 'info-requests',
                    children: [
                      {
                        path: '',
                        async lazy() {
                          const { GuardValidationRequestsPage } = await import(
                            '@modules/project-info-requests/pages/view'
                          )
                          return { Component: GuardValidationRequestsPage }
                        },
                      },
                      {
                        path: 'details/:infoRequestId',
                        async lazy() {
                          const { DetailsPage } = await import('@modules/project-info-requests/pages/details')
                          return { Component: DetailsPage }
                        },
                      },
                    ],
                  },
                  {
                    path: 'appointment-requests',
                    children: [
                      {
                        path: '',
                        async lazy() {
                          const { GuardValidationRequestsPage } = await import(
                            '@modules/project-appointment-requests/pages/view'
                          )
                          return { Component: GuardValidationRequestsPage }
                        },
                      },
                      {
                        path: 'details/:appointmentRequestId',
                        async lazy() {
                          const { DetailsPage } = await import('@modules/project-appointment-requests/pages/details')
                          return { Component: DetailsPage }
                        },
                      },
                      {
                        path: 'proposals/:proposalId',
                        async lazy() {
                          const { AppointmentProposalDetailsPage } = await import(
                            '@modules/project-appointment-requests/pages/proposal-details'
                          )
                          return { Component: AppointmentProposalDetailsPage }
                        },
                      },
                    ],
                  },
                  {
                    path: 'announcements',
                    children: [
                      {
                        path: '',
                        async lazy() {
                          const { GuardAnnouncementsPage } = await import('@modules/project-announcements/pages/view')
                          return { Component: GuardAnnouncementsPage }
                        },
                      },
                      {
                        path: 'details/:announcementId',
                        async lazy() {
                          const { AnnouncementDetailsPage } = await import(
                            '@modules/project-announcements/pages/details'
                          )
                          return { Component: AnnouncementDetailsPage }
                        },
                      },
                    ],
                  },
                  {
                    path: 'suivi',
                    async lazy() {
                      const { GuardSuiviPage } = await import('@modules/project-suivi/pages/view')
                      return { Component: GuardSuiviPage }
                    },
                  },
                  {
                    path: '',
                    Component: () => <Navigate to="view" />,
                  },
                ],
              },
              {
                path: 'settings',
                async lazy() {
                  const { GuardMainPage } = await import('@modules/settings/pages/main')
                  return { Component: GuardMainPage }
                },
                children: [
                  {
                    path: 'my_account',
                    async lazy() {
                      const { GuardMyAccountPage } = await import('@modules/settings/pages/my-account')
                      return { Component: GuardMyAccountPage }
                    },
                  },
                  {
                    path: 'subscriptions',
                    async lazy() {
                      const { GuardSubscriptionsPage } = await import('@modules/settings/pages/subscriptions')
                      return { Component: GuardSubscriptionsPage }
                    },
                  },
                  {
                    path: 'payment_methods',
                    async lazy() {
                      const { GuardPaymentMethodsPage } = await import('@modules/settings/pages/payment-methods')
                      return { Component: GuardPaymentMethodsPage }
                    },
                  },
                  {
                    path: 'invoices',
                    async lazy() {
                      const { GuardInvoicesPage } = await import('@modules/settings/pages/invoices')
                      return { Component: GuardInvoicesPage }
                    },
                  },
                ],
              },
              {
                path: 'admin',
                async lazy() {
                  const { GuardMainPage } = await import('@modules/admin/pages/main')
                  return { Component: GuardMainPage }
                },
                children: [
                  {
                    path: 'users',
                    async lazy() {
                      const { UsersPage } = await import('@modules/admin/pages/users')
                      return { Component: UsersPage }
                    },
                  },
                  {
                    path: 'projects',
                    async lazy() {
                      const { ProjectsPage } = await import('@modules/admin/pages/projects')
                      return { Component: ProjectsPage }
                    },
                  },
                ],
              },
              {
                path: '',
                Component: () => <Navigate to="projects" />,
              },
            ],
          },
          {
            path: 'sign-in',
            async lazy() {
              const { SignInPage } = await import('@modules/auth/pages/sign-in')
              return { Component: SignInPage }
            },
          },
          {
            path: 'sms-link-unavailable',
            async lazy() {
              const { SmsLinkUnavailablePage } = await import('@modules/main/pages/sms-link-unavailable')
              return { Component: SmsLinkUnavailablePage }
            },
          },
          {
            path: 'sign-up',
            async lazy() {
              const { SignUpPage } = await import('@modules/auth/pages/sign-up')
              return { Component: SignUpPage }
            },
          },
          // ponytail: route de bench DEV-only, non gardée (pas d'auth) — harnais perf Bryntum e2e (CODI-496).
          // Sans `:projectId`, ReadOnlyPlanning bascule sur le jeu synthétique via `?bench=<n>`.
          // Absente du build prod (`import.meta.env.DEV` false → route non montée).
          ...(import.meta.env.DEV
            ? [
                {
                  path: 'planning-bench',
                  async lazy() {
                    const { BryntumTestPage } = await import('@modules/project-planning/pages/bryntum-test')
                    return { Component: BryntumTestPage }
                  },
                },
              ]
            : []),
          {
            path: 'join/:projectSharingId',
            async lazy() {
              const { JoinPage } = await import('@modules/sharing/pages/join')
              return { Component: JoinPage }
            },
          },
          {
            path: 'project-validation-request',
            async lazy() {
              const { ProjectValidationRequestPage } = await import(
                '@modules/project-validation-requests/pages/validation-request'
              )
              return { Component: ProjectValidationRequestPage }
            },
          },
          {
            path: 'project-info-request',
            async lazy() {
              const { ProjectValidationRequestInfoPage } = await import(
                '@modules/project-info-requests/pages/validation-request-info'
              )
              return { Component: ProjectValidationRequestInfoPage }
            },
          },
          {
            path: 'project-appointment-proposal',
            async lazy() {
              const { PublicAppointmentProposalPage } = await import(
                '@modules/project-appointment-requests/pages/public-proposal'
              )
              return { Component: PublicAppointmentProposalPage }
            },
          },
          {
            path: 'project-appointment-request',
            async lazy() {
              const { ProjectValidationRequestAppointmentPage } = await import(
                '@modules/project-appointment-requests/pages/validation-request-appointment'
              )
              return { Component: ProjectValidationRequestAppointmentPage }
            },
          },
          {
            path: 'project-announcement',
            async lazy() {
              const { PublicAnnouncementPage } = await import(
                '@modules/project-announcements/pages/public-announcement'
              )
              return { Component: PublicAnnouncementPage }
            },
          },

          {
            path: ':not_found',
            Component: () => {
              const { i18n } = useTranslation()
              return <Navigate to={`/${i18n.language}`} />
            },
          },
        ],
      },
    ],
  },
]
